import { ref, computed, readonly, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useChartStore } from '@/stores/chartStore';
import { useLayoutStore } from '@/stores/layoutStore';
import { useSavedChartsStore } from '@/stores/savedChartsStore';
import { useLoadSymbolOnly } from '@/composables/useLoadSymbolOnly';
import { useChartNotesV2 } from '@/composables/useChartNotesV2';
import { useLoadChartsAndChartStyles } from '@/composables/useLoadChartsAndChartStyles';
import { loadChartsForChartList } from '@/composables/loadChartsForChartList';
import { isValidChartRecid } from '@/composables/isValidChartRecid';
import { isValidChartStyleRecid } from '@/composables/isValidChartStyleRecid';
import { INVALID_CHART_LIST_LIST_NUM } from '@/composables/isValidChartListListNum';
import { INVALID_CHART_LIST_RECID } from '@/composables/isValidChartListRecid';
import { getChart } from '@/composables/getChart';
import { ChartList } from '@/interfaces/chartList';
import { SimpleFrontendChart } from '@/interfaces/chart';

const selectedChartListRecid = ref<number>(INVALID_CHART_LIST_RECID);

let hasInitialized = false;

const selectedChartList = computed<ChartList | null>(() => {
  const savedChartsStore = useSavedChartsStore();

  return savedChartsStore.chartLists.find(chartList => chartList.recid === selectedChartListRecid.value) ?? null;
});

const selectedChartListCharts = computed<SimpleFrontendChart[]>(() => {
  return selectedChartList.value?.charts ?? [];
});

const chartListContainingChartBeingViewed = computed<ChartList | null>(() => {
  // TODO: do we need to do a comparison of chart.symbol to chartStore.symbol here?
  const chartStore = useChartStore();
  const savedChartsStore = useSavedChartsStore();

  if (!isValidChartRecid(chartStore.recidOfChartBeingViewed)) {
    return null;
  }

  for (const chartList of savedChartsStore.chartLists) {
    for (const chart of chartList.charts) {
      if (chart.recid === chartStore.recidOfChartBeingViewed) {
        return chartList;
      }
    }
  }

  return null;
});

const chartBeingViewed = computed<SimpleFrontendChart | null>(() => {
  // TODO: do we need to do a comparison of chart.symbol to chartStore.symbol here?
  const chartStore = useChartStore();

  if (!chartListContainingChartBeingViewed.value) {
    return null;
  }

  for (const chart of chartListContainingChartBeingViewed.value.charts) {
    if (chart.recid === chartStore.recidOfChartBeingViewed) {
      return chart;
    }
  }

  return null;
});

const isChartBeingViewedReadonly = computed<boolean>(() => {
  return chartListContainingChartBeingViewed.value?.isReadonly ?? false;
});

const loadSymbolOnlyChartBeingViewed = computed(() => {
  // TODO: do we need to do a comparison of chart.symbol to chartStore.symbol here?
  const chartStore = useChartStore();
  const savedChartsStore = useSavedChartsStore();

  if (!isValidChartRecid(chartStore.recidOfLoadSymbolOnlyChartBeingViewed)) {
    return null;
  }

  for (const chartList of savedChartsStore.chartLists) {
    for (const chart of chartList.charts) {
      if (chart.recid === chartStore.recidOfLoadSymbolOnlyChartBeingViewed) {
        return chart;
      }
    }
  }

  return null;
});

const chartStyleBeingViewed = computed(() => {
  const chartStore = useChartStore();
  const savedChartsStore = useSavedChartsStore();

  if (!isValidChartStyleRecid(chartStore.recidOfChartStyleBeingViewed, true)) {
    return null;
  }

  const chartStyle = savedChartsStore.chartStyles.find(cs => cs.recid === chartStore.recidOfChartStyleBeingViewed) ?? null;

  if (chartStyle) {
    return chartStyle;
  }

  for (const sampleChartStyleGroup of savedChartsStore.sampleChartStyleGroups) {
    for (const sampleChartStyle of sampleChartStyleGroup.charts) {
      if (sampleChartStyle.recid === chartStore.recidOfChartStyleBeingViewed) {
        return sampleChartStyle;
      }
    }
  }

  return null;
});

const styleCacheIdOfChartBeingViewed = computed(() => {
  return chartBeingViewed.value?.styleCacheId ?? '';
});

const styleCacheIdOfChartStyleBeingViewed = computed(() => {
  return chartStyleBeingViewed.value?.styleCacheId ?? '';
});

const listNumOfChartListContainingChartBeingViewed = computed(() => {
  return chartBeingViewed.value?.chartListListNum ?? INVALID_CHART_LIST_LIST_NUM;
});

const styleCacheSettingsOverridesOfChartBeingViewed = computed(() => {
  return chartBeingViewed.value?.styleCacheSettingsOverrides ?? {};
});

const styleCacheSettingsOverridesOfChartStyleBeingViewed = computed(() => {
  return chartStyleBeingViewed.value?.styleCacheSettingsOverrides ?? {};
});

const selectedChartInSelectedChartList = computed(() => {
  const { isLoadSymbolOnlyEnabled } = useLoadSymbolOnly();

  const chart = isLoadSymbolOnlyEnabled.value ? loadSymbolOnlyChartBeingViewed.value : chartBeingViewed.value;

  if (
    !chart ||
    chart.chartListListNum !== selectedChartList.value?.listNum
  ) {
    return null;
  }

  return chart;
});

const isSelectedChartInSelectedChartListReadonly = computed<boolean>(() => {
  return selectedChartList.value?.isReadonly ?? false;
});

async function selectChartList(chartList: ChartList) {
  const { isLoadSymbolOnlyEnabled } = useLoadSymbolOnly();
  const { loadSimpleFrontendChart } = useLoadChartsAndChartStyles();

  selectedChartListRecid.value = chartList.recid;

  await loadChartsForChartList(chartList.recid, undefined, false);

  if (selectedChartListRecid.value !== chartList.recid) {
    return;
  }

  const firstChartInChartListThatWillNotRedirectAwayFromTheSC3Workbench = chartList.charts.find(chart => !chart.isPointAndFigureChart || isLoadSymbolOnlyEnabled.value);

  if (!firstChartInChartListThatWillNotRedirectAwayFromTheSC3Workbench) {
    return;
  }

  await loadSimpleFrontendChart(firstChartInChartListThatWillNotRedirectAwayFromTheSC3Workbench, { shouldScrollToChartOnSuccess: false, shouldEnableArrowHotkeys: true });
}

function setSelectedChartListRecid(num: number) {
  selectedChartListRecid.value = num;
}

async function loadPreviousChart() {
  const chartStore = useChartStore();
  const { isLoadSymbolOnlyEnabled } = useLoadSymbolOnly();
  const { loadSimpleFrontendChart } = useLoadChartsAndChartStyles();

  if (!selectedChartListCharts.value.length) {
    return;
  }

  let previousChartIndex = selectedChartListCharts.value.length - 1;

  for (let i = 0; i < selectedChartListCharts.value.length; i++) {
    const chart = selectedChartListCharts.value[i];

    if (
      (!isLoadSymbolOnlyEnabled.value && chart.recid === chartStore.recidOfChartBeingViewed) ||
      (isLoadSymbolOnlyEnabled.value && chart.recid === chartStore.recidOfLoadSymbolOnlyChartBeingViewed)
    ) {
      previousChartIndex = i - 1;
      break;
    }
  }

  if (previousChartIndex < 0) {
    previousChartIndex = selectedChartListCharts.value.length - 1;
  }

  await loadSimpleFrontendChart(selectedChartListCharts.value[previousChartIndex], { shouldScrollToChartOnSuccess: false, shouldEnableArrowHotkeys: true });
}

async function loadNextChart() {
  const chartStore = useChartStore();
  const { isLoadSymbolOnlyEnabled } = useLoadSymbolOnly();
  const { loadSimpleFrontendChart } = useLoadChartsAndChartStyles();

  if (!selectedChartListCharts.value.length) {
    return;
  }

  let nextChartIndex = 0;

  for (let i = 0; i < selectedChartListCharts.value.length; i++) {
    const chart = selectedChartListCharts.value[i];

    if (
      (!isLoadSymbolOnlyEnabled.value && chart.recid === chartStore.recidOfChartBeingViewed) ||
      (isLoadSymbolOnlyEnabled.value && chart.recid === chartStore.recidOfLoadSymbolOnlyChartBeingViewed)
    ) {
      nextChartIndex = i + 1;
      break;
    }
  }

  if (nextChartIndex >= selectedChartListCharts.value.length) {
    nextChartIndex = 0;
  }

  await loadSimpleFrontendChart(selectedChartListCharts.value[nextChartIndex], { shouldScrollToChartOnSuccess: false, shouldEnableArrowHotkeys: true });
}

async function getChartListContainingChart(chartRecid: number) {
  const savedChartsStore = useSavedChartsStore();

  if (!isValidChartRecid(chartRecid)) {
    return null;
  }

  const refreshedChart = await getChart(chartRecid);

  if (!refreshedChart) {
    return null;
  }

  await loadChartsForChartList(undefined, refreshedChart.chartListListNum, false);

  for (const chartList of savedChartsStore.chartLists) {
    for (const chart of chartList.charts) {
      if (chart.recid === refreshedChart.recid) {
        return chartList;
      }
    }
  }

  return null;
}

async function handleChartListsChanged() {
  const chartStore = useChartStore();
  const savedChartsStore = useSavedChartsStore();
  const { isLoadSymbolOnlyEnabled } = useLoadSymbolOnly();

  if (selectedChartList.value) {
    return;
  }

  const chartRecidCopy = isLoadSymbolOnlyEnabled.value ? chartStore.recidOfLoadSymbolOnlyChartBeingViewed : chartStore.recidOfChartBeingViewed;
  let chartList = await getChartListContainingChart(chartRecidCopy);
  const chartRecid = isLoadSymbolOnlyEnabled.value ? chartStore.recidOfLoadSymbolOnlyChartBeingViewed : chartStore.recidOfChartBeingViewed;

  if (chartRecidCopy !== chartRecid) {
    return;
  }

  if (!chartList) {
    chartList = savedChartsStore.chartLists[0] ?? null;
  }

  if (chartList) {
    setSelectedChartListRecid(chartList.recid);
  }
}

async function handleSelectedChartListChanged() {
  if (!selectedChartList.value) {
    return;
  }

  await loadChartsForChartList(selectedChartList.value.recid, undefined, false);
}

async function setSelectedChartListRecidToChartListContainingCurrentlyViewedChart() {
  const chartStore = useChartStore();
  const { isLoadSymbolOnlyEnabled } = useLoadSymbolOnly();

  const chartRecidCopy = isLoadSymbolOnlyEnabled.value ? chartStore.recidOfLoadSymbolOnlyChartBeingViewed : chartStore.recidOfChartBeingViewed;
  const chartList = await getChartListContainingChart(chartRecidCopy);
  const chartRecid = isLoadSymbolOnlyEnabled.value ? chartStore.recidOfLoadSymbolOnlyChartBeingViewed : chartStore.recidOfChartBeingViewed;

  if (
    !chartList ||
    chartRecidCopy !== chartRecid
  ) {
    return;
  }

  setSelectedChartListRecid(chartList.recid);
}

async function handleKeyDownEvent(e: KeyboardEvent) {
  const layoutStore = useLayoutStore();
  const { isActivatingAnnotationMode, isAnnotating } = useChartNotesV2();

  if (
    !e.ctrlKey ||
    e.shiftKey ||
    e.metaKey ||
    layoutStore.modalType ||
    layoutStore.isDragNDropping ||
    isActivatingAnnotationMode.value ||
    isAnnotating.value
  ) {
    return;
  }

  if (e.key === '.') {
    await loadNextChart();
  }

  if (e.key === ',') {
    await loadPreviousChart();
  }
}

function init() {
  const chartStore = useChartStore();
  const savedChartsStore = useSavedChartsStore();
  const { isLoadSymbolOnlyEnabled } = useLoadSymbolOnly();

  const { recidOfChartBeingViewed, recidOfLoadSymbolOnlyChartBeingViewed } = storeToRefs(chartStore);
  const { chartLists } = storeToRefs(savedChartsStore);


  watch(chartLists, handleChartListsChanged);
  watch(selectedChartList, handleSelectedChartListChanged);
  watch(recidOfChartBeingViewed, setSelectedChartListRecidToChartListContainingCurrentlyViewedChart);
  watch(recidOfLoadSymbolOnlyChartBeingViewed, setSelectedChartListRecidToChartListContainingCurrentlyViewedChart);
  watch(isLoadSymbolOnlyEnabled, setSelectedChartListRecidToChartListContainingCurrentlyViewedChart);
  watch(listNumOfChartListContainingChartBeingViewed, setSelectedChartListRecidToChartListContainingCurrentlyViewedChart);

  document.body.addEventListener('keydown', handleKeyDownEvent);

  hasInitialized = true;
}

export function useWorkbenchSelections() {
  if (!hasInitialized)
    init();

  return {
    selectedChartListRecid: readonly(selectedChartListRecid),
    selectChartList,
    loadPreviousChart,
    loadNextChart,
    selectedChartList,
    selectedChartListCharts,
    chartListContainingChartBeingViewed,
    chartBeingViewed,
    isChartBeingViewedReadonly,
    loadSymbolOnlyChartBeingViewed,
    styleCacheIdOfChartBeingViewed,
    styleCacheIdOfChartStyleBeingViewed,
    styleCacheSettingsOverridesOfChartBeingViewed,
    styleCacheSettingsOverridesOfChartStyleBeingViewed,
    selectedChartInSelectedChartList,
    isSelectedChartInSelectedChartListReadonly
  };
}
